import { useEffect, useRef, useState } from "react";
import { animate, useInView } from "framer-motion";
import { Rise, Words } from "../components/Reveal";
import { COPY } from "../content/copy";
import { ImageTrigger } from "../components/viewer/ImageTrigger";
import { caseImages } from "../components/viewer/galleries";

// "1.434", "90,6K €", "2,9%" → il numero conta da zero quando entra nello schermo
const parseValue = (v) => {
    const m = v.match(/^([^\d]*)([\d.]+(?:,\d+)?)(.*)$/);
    if (!m) return null;
    const num = parseFloat(m[2].replace(/\./g, "").replace(",", "."));
    const decimals = (m[2].split(",")[1] || "").length;
    return { prefix: m[1], num, decimals, suffix: m[3] };
};
const fmt = (n, d) => {
    const [int, dec] = n.toFixed(d).split(".");
    return int.replace(/\B(?=(\d{3})+(?!\d))/g, ".") + (dec ? `,${dec}` : "");
};

const Count = ({ value }) => {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-10% 0px" });
    const parsed = parseValue(value);
    const [shown, setShown] = useState(parsed ? fmt(0, parsed.decimals) : value);
    useEffect(() => {
        if (!inView || !parsed) return;
        const ctrl = animate(0, parsed.num, {
            duration: 1.6, ease: [0.16, 1, 0.3, 1],
            onUpdate: (v) => setShown(fmt(v, parsed.decimals)),
        });
        return () => ctrl.stop();
    }, [inView]); // eslint-disable-line react-hooks/exhaustive-deps
    if (!parsed) return <span ref={ref}>{value}</span>;
    return <span ref={ref} data-count={value}>{parsed.prefix}{shown}{parsed.suffix}</span>;
};

// i numeri dallo schermo: registro tipografico + lo screenshot vero, cliccabile
const CasoMetrics = ({ m, i, images }) => (
    <div className="caso-metrics" data-testid={`caso-metrics-${i}`}>
        <dl className="ledger">
            {m.rows.map((r) => (
                <div className="ledger-row" key={r.l}>
                    <dt className="caption">{r.l}</dt>
                    <dd>
                        <span className="ledger-v"><Count value={r.v} /></span>
                        {r.d && <span className="ledger-d">{r.d}</span>}
                    </dd>
                </div>
            ))}
        </dl>
        <div className="shots">
            {m.shots.map((s, j) => (
                <ImageTrigger className="shot" key={s.strip} images={images} index={j} testId={`caso-shot-${i}-${j}`}>
                    <img src={s.strip} alt={s.alt} loading="lazy" />
                </ImageTrigger>
            ))}
        </div>
        <p className="caption shots-source">{m.source} · dallo schermo</p>
    </div>
);

export default function Casi() {
    const c = COPY.casi;
    return (
        <section className="section" data-testid="casi-block">
            <div className="container">
                <Rise>
                    <p className="caption">{c.caption}</p>
                </Rise>
                <Rise delay={0.05}>
                    <h2 className="h2 section-title" data-testid="casi-title">
                        <Words text={c.titleA} />{" "}
                        <Words text={c.titleB} italic />
                    </h2>
                </Rise>
                <Rise delay={0.1}>
                    <p className="lead section-lead">{c.lead}</p>
                </Rise>
                <div className="grid-hairline casi-grid">
                    {c.items.map((item, i) => (
                        <Rise
                            key={i}
                            delay={i * 0.1}
                            className="caso-cell"
                        >
                            <p className="caption">{item.meta}</p>
                            <h3 className="caso-title" data-testid={`caso-title-${i}`}>
                                {item.title}
                            </h3>
                            <p className="caso-text">{item.text}</p>
                            {item.metrics && <CasoMetrics m={item.metrics} i={i} images={caseImages(item)} />}
                            {item.photo && (
                                <figure className="caso-photo" data-testid={`caso-photo-${i}`}>
                                    <ImageTrigger images={caseImages(item)} index={item.metrics?.shots.length || 0} testId={`caso-photo-open-${i}`}>
                                        <img src={item.photo.src} alt={item.photo.alt} loading="lazy" />
                                    </ImageTrigger>
                                    <figcaption className="caption">{item.photo.label}</figcaption>
                                </figure>
                            )}
                            <p className="ital caso-result" data-testid={`caso-result-${i}`}>
                                {item.result}
                            </p>
                        </Rise>
                    ))}
                </div>
            </div>
        </section>
    );
}
